const WEEK_START_DAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const JS_DAY_BY_NAME = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};
const JS_DAY_BY_KEY = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

export const DEFAULT_WEEK_START_DAY = 'MONDAY';
export const DEFAULT_WEEK_END_DAY = 'SUNDAY';

export const WEEK_START_DAY_OPTIONS = WEEK_START_DAYS.map((value, index) => ({
  value,
  label: DAY_LABELS[index],
}));

export const normalizeWeekStartDay = (value) => {
  const key = String(value || DEFAULT_WEEK_START_DAY).trim().toUpperCase();
  return WEEK_START_DAYS.includes(key) ? key : DEFAULT_WEEK_START_DAY;
};

export const normalizeWeekEndDay = (value) => {
  const key = String(value || DEFAULT_WEEK_END_DAY).trim().toUpperCase();
  return WEEK_START_DAYS.includes(key) ? key : DEFAULT_WEEK_END_DAY;
};

export const parseTenantWorkWeek = (payload) => ({
  weekStartDay: normalizeWeekStartDay(payload?.week_start_day || payload?.weekStartDay),
  weekEndDay: normalizeWeekEndDay(payload?.week_end_day || payload?.weekEndDay),
});

export const rotateWeekDays = (startDay) => {
  const idx = WEEK_START_DAYS.indexOf(normalizeWeekStartDay(startDay));
  const start = idx < 0 ? 0 : idx;
  return {
    dayLabels: [...DAY_LABELS.slice(start), ...DAY_LABELS.slice(0, start)],
    dayKeys: [...DAY_KEYS.slice(start), ...DAY_KEYS.slice(0, start)],
  };
};

export const buildWorkWeekDays = (startDay, endDay) => {
  const start = normalizeWeekStartDay(startDay);
  const end = normalizeWeekEndDay(endDay);
  const startIdx = WEEK_START_DAYS.indexOf(start);
  const endIdx = WEEK_START_DAYS.indexOf(end);
  const days = [];
  let i = startIdx < 0 ? 0 : startIdx;
  const stop = endIdx < 0 ? 6 : endIdx;
  while (days.length < 7) {
    days.push({
      value: WEEK_START_DAYS[i],
      label: DAY_LABELS[i],
      key: DAY_KEYS[i],
    });
    if (i === stop) break;
    i = (i + 1) % 7;
  }
  return {
    days,
    dayLabels: days.map((d) => d.label),
    dayKeys: days.map((d) => d.key),
  };
};

export const formatWorkWeekRangeLabel = (startDay, endDay) => {
  const { dayLabels } = buildWorkWeekDays(startDay, endDay);
  if (!dayLabels.length) return '';
  return `${dayLabels[0]} – ${dayLabels[dayLabels.length - 1]}`;
};

export const getWeekStartDate = (baseDate, startDay) => {
  const d = new Date(baseDate);
  d.setHours(0, 0, 0, 0);
  const target = JS_DAY_BY_NAME[normalizeWeekStartDay(startDay)];
  const diff = (d.getDay() - target + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
};

export const getMonthGridStartOffset = (firstOfMonth, startDay) => {
  const target = JS_DAY_BY_NAME[normalizeWeekStartDay(startDay)];
  return (firstOfMonth.getDay() - target + 7) % 7;
};

export const dateForCoverageDayInWeek = (weekStartDate, dayPrefix) => {
  const start = weekStartDate instanceof Date
    ? new Date(weekStartDate)
    : new Date(`${weekStartDate}T00:00:00`);
  start.setHours(0, 0, 0, 0);
  const target = JS_DAY_BY_KEY[String(dayPrefix || '').toLowerCase()];
  if (target == null) return start;
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    if (d.getDay() === target) return d;
  }
  return start;
};

export const fetchTenantWorkWeek = async (backendBase, token, tenantId) => {
  const fallback = {
    weekStartDay: DEFAULT_WEEK_START_DAY,
    weekEndDay: DEFAULT_WEEK_END_DAY,
  };
  if (!tenantId) return fallback;
  const base = backendBase || '';
  const resp = await fetch(
    `${base}conductor/workforce-tenant-week?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  if (!resp.ok) return fallback;
  const payload = await resp.json();
  return parseTenantWorkWeek(payload);
};

export const fetchTenantWeekStartDay = async (backendBase, token, tenantId) => {
  const week = await fetchTenantWorkWeek(backendBase, token, tenantId);
  return week.weekStartDay;
};

export const saveTenantWorkWeek = async (backendBase, token, tenantId, weekStartDay, weekEndDay) => {
  const base = backendBase || '';
  const resp = await fetch(`${base}conductor/workforce-tenant-week`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      tenant_id: tenantId,
      week_start_day: normalizeWeekStartDay(weekStartDay),
      week_end_day: normalizeWeekEndDay(weekEndDay),
    }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(text || `Failed to save work week (${resp.status})`);
  }
  const payload = await resp.json();
  return parseTenantWorkWeek(payload);
};
